/**
 * Bottom-right stack, max 3, `bg-raised`, 1px `border-default`,
 * `shadow-popover`, `radius-lg`, 4px left border in the status colour,
 * auto-dismiss 5s with **errors persisting** (components.md §3).
 *
 * The entrance is the one place `ease-spring` is used, and it is reserved for
 * fill toasts: a fill is the moment the product did the thing the user asked
 * for, and it is the only event worth a bounce. Everything else enters on
 * `ease-out`.
 *
 * The dismissal timer lives here, not in the store. A store that owns a
 * `setTimeout` is a store that leaks one, and `setTimeout` is deliberately not
 * `setInterval` — Appendix B of docs/03 is the complete list of *recurring*
 * timers and a one-shot dismissal is not one of them.
 */

import { useEffect } from 'react'
import { cx } from '@/lib/cx'
import { useStore } from '@/app/use-store'
import { dismissToast, toastStore } from '@/stores/ui'
import type { Toast as ToastModel, ToastTone } from '@/stores/ui'

export const TOAST_DISMISS_MS = 5_000

const TONE: Record<ToastTone, { bar: string; title: string }> = {
  info: { bar: 'bg-info-solid', title: 'text-text-primary' },
  success: { bar: 'bg-success-solid', title: 'text-up-text' },
  warning: { bar: 'bg-warning-solid', title: 'text-warning-text' },
  danger: { bar: 'bg-danger-solid', title: 'text-down-text' },
}

export function Toast({ toast, spring = false }: { toast: ToastModel; spring?: boolean }) {
  const dismissMs = toast.dismissMs ?? TOAST_DISMISS_MS
  useEffect(() => {
    if (toast.persist) return
    const handle = globalThis.setTimeout(() => dismissToast(toast.id), dismissMs)
    return () => globalThis.clearTimeout(handle)
  }, [toast.id, toast.persist, dismissMs])

  const tone = TONE[toast.tone]

  return (
    <div
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      data-tone={toast.tone}
      className={cx(
        // 320px, or the screen less the host's 16px on each side, whichever is
        // narrower — the host is `right-4`, so on a 320px phone a fixed width
        // hung off the left edge.
        'pointer-events-auto relative flex w-[min(calc(320*var(--ud-u)),calc(100vw-32*var(--ud-u)))] gap-2 overflow-hidden rounded-lg border border-border-default bg-bg-raised p-2 pl-3 shadow-popover',
        'motion-safe:animate-[ud-toast-in_180ms_both]',
        spring ? '[animation-timing-function:var(--ud-ease-spring)]' : 'ease-out',
      )}
    >
      <span aria-hidden="true" className={cx('absolute inset-y-0 left-0 w-1', tone.bar)} />
      <div className="flex min-w-0 flex-1 flex-col gap-0_5">
        <span className={cx('text-body font-semibold', tone.title)}>{toast.title}</span>
        {toast.detail ? <span className="ud-num text-body-sm text-text-primary">{toast.detail}</span> : null}
        {toast.body ? <span className="text-body-sm text-text-secondary">{toast.body}</span> : null}
        {/* `docs/07`'s bracketed remedy. Dismisses on the way out — leaving the
            toast up after the user has acted on it makes the action look
            un-taken. */}
        {toast.action ? (
          <button
            type="button"
            onClick={() => {
              toast.action?.run()
              dismissToast(toast.id)
            }}
            className="inline-flex min-h-[var(--ud-tap-min)] md:min-h-0 items-center self-start rounded-sm text-body-sm font-semibold text-accent-text underline-offset-2 hover:underline focus-visible:shadow-focus focus-visible:outline-none"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="dismiss"
        onClick={() => dismissToast(toast.id)}
        className="inline-flex shrink-0 min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0 items-center justify-center self-start rounded-sm px-1 text-body-sm text-text-tertiary hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
      >
        ×
      </button>
    </div>
  )
}

/**
 * The host. Mounted once by `<Providers>`, reads the store, renders at most
 * three. `aria-live="polite"` on the region rather than on each toast, so three
 * arriving at once are announced in order instead of interrupting each other.
 */
export function ToastHost() {
  const { toasts } = useStore(toastStore)

  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} spring={t.tone === 'success'} />
      ))}
    </div>
  )
}
